import { createMachine } from "xstate";
import { activateOrder, cancelOrder } from "@nju/data-access-portal-order";
import { repeatOrchestratorPayment } from "@nju/data-access-portal-payment";
import { BasicError, isError } from "@nju/result";

interface IContext {
  orchestratorId: string;
}

type IEvent =
  | { type: "ACTIVATE_ORDER" }
  | { type: "ACTIVATE_SUCCESS" }
  | { type: "ACTIVATE_ERROR" }
  | { type: "CANCEL_ORDER" }
  | { type: "CANCEL_ORDER_SUCCESS" }
  | { type: "CANCEL_ORDER_ERROR" }
  | { type: "RETRY_PAYMENT" }
  | { type: "RETRY_PAYMENT_ERROR" }
  | { type: "RETRY_PAYMENT_SUCCESS" }
  | { type: "DW_INSUFFICIENT_FUNDS" }
  | { type: "MIGRATION_CONFIRM_SUCCESS" }
  | { type: "MIGRATION_CONFIRM_ERROR" };

export const mobileStatusMachine = createMachine(
  {
    schema: {
      context: {} as IContext,
      events: {} as IEvent,
    },
    context: {
      orchestratorId: "",
    },
    initial: "ready",
    states: {
      ready: {
        on: {
          ACTIVATE_ORDER: {
            target: "activate",
          },
          CANCEL_ORDER: {
            target: "cancel",
          },
          RETRY_PAYMENT: {
            target: "retryPayment",
          },
        },
      },
      retryPayment: {
        invoke: {
          src: "retryPayment",
        },
        on: {
          RETRY_PAYMENT_SUCCESS: {
            actions: "onRetryPaymentSuccess",
            type: "final",
          },
          RETRY_PAYMENT_ERROR: {
            target: "error",
          },
          DW_INSUFFICIENT_FUNDS: {
            actions: "onDWInsufficientFunds",
          },
        },
      },
      activate: {
        invoke: {
          src: "activateCard",
        },
        on: {
          ACTIVATE_SUCCESS: {
            actions: "onActivateSuccess",
          },
          ACTIVATE_ERROR: {
            target: "error",
          },
          DW_INSUFFICIENT_FUNDS: {
            actions: "onDWInsufficientFunds",
          },
        },
      },
      cancel: {
        invoke: {
          src: "cancelOrder",
        },
        on: {
          CANCEL_ORDER_SUCCESS: {
            actions: "onCancelOrderSuccess",
          },
          CANCEL_ORDER_ERROR: {
            target: "error",
          },
        },
      },
      error: {
        type: "final",
      },
      final: {
        type: "final",
      },
    },
  },
  {
    services: {
      retryPayment: ({ orchestratorId }) => {
        return async (callback) => {
          if (!orchestratorId) {
            throw new BasicError({
              message: "Missing orchestratorId",
            });
          }

          const result = await repeatOrchestratorPayment({
            orchestratorId,
          });

          if (!isError(result)) {
            return callback({ type: "RETRY_PAYMENT_SUCCESS" });
          }

          if (result.err.code === "DW_INSUFFICIENT_FUNDS") {
            return callback({ type: "DW_INSUFFICIENT_FUNDS" });
          }

          callback({ type: "RETRY_PAYMENT_ERROR" });
        };
      },
      activateCard: ({ orchestratorId }) => {
        return async (callback) => {
          const result = await activateOrder({
            orchestratorId,
          });

          if (!isError(result)) {
            return callback({ type: "ACTIVATE_SUCCESS" });
          }

          if (result.err.code === "DW_INSUFFICIENT_FUNDS") {
            return callback({ type: "DW_INSUFFICIENT_FUNDS" });
          }

          callback({ type: "ACTIVATE_ERROR" });
        };
      },
      cancelOrder: ({ orchestratorId }) => {
        return async (callback) => {
          const result = await cancelOrder({
            reason: "userRequest",
            orchestratorId,
            mode: "mobile",
          });

          if (!isError(result)) {
            return callback({ type: "CANCEL_ORDER_SUCCESS" });
          }

          callback({ type: "CANCEL_ORDER_ERROR" });
        };
      },
    },
  },
);
